import React, { useState, useEffect, useCallback } from 'react';
import { fetchStats, fetchInfluencers } from '../api';
import { fmtK, fmtPct, COLORS, TIER_COLORS } from '../constants';

function StatCard({ label, value, sub, active, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: active ? 'rgba(229,93,74,0.12)' : '#242420',
      border: `0.5px solid ${active ? '#E55D4A' : 'rgba(245,235,221,0.08)'}`,
      borderRadius: 12, padding: '1rem 1.25rem',
      cursor: onClick ? 'pointer' : 'default', transition: 'all 0.15s',
    }}>
      <div style={{ fontSize:10, color:'rgba(245,235,221,0.4)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:600, color: active?'#F5EBDD':'#F5EBDD', letterSpacing:-0.5 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'rgba(245,235,221,0.3)', marginTop:3 }}>{sub}</div>}
    </div>
  );
}

function BarRow({ labelKey, item, total, color, active, onClick }) {
  const label = item[labelKey];
  const pct   = total > 0 ? Math.round((item.count/total)*100) : 0;
  return (
    <div onClick={onClick} style={{ marginBottom:10, cursor:'pointer', padding:'5px 8px', borderRadius:7, background:active?'rgba(245,235,221,0.05)':'transparent', transition:'background 0.15s' }}
      onMouseEnter={e=>{if(!active)e.currentTarget.style.background='rgba(255,255,255,0.03)'}}
      onMouseLeave={e=>{if(!active)e.currentTarget.style.background=active?'rgba(245,235,221,0.05)':'transparent'}}
    >
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          {active && <span style={{ width:5, height:5, borderRadius:'50%', background:color, display:'inline-block', flexShrink:0 }}/>}
          <span style={{ fontSize:12, color:active?'#F5EBDD':'rgba(255,255,255,0.75)', fontWeight:active?600:400 }}>{label}</span>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          {item.reach !== undefined && <span style={{ fontSize:10, color:'rgba(245,235,221,0.3)' }}>{fmtK(item.reach)}</span>}
          <span style={{ fontSize:12, fontWeight:600, color }}>{item.count} <span style={{ fontSize:10, color:'rgba(245,235,221,0.3)', fontWeight:400 }}>({pct}%)</span></span>
        </div>
      </div>
      <div style={{ height:4, background:'rgba(245,235,221,0.06)', borderRadius:2 }}>
        <div style={{ height:4, width:`${pct}%`, background:active?color:`${color}88`, borderRadius:2, transition:'width 0.5s ease' }}/>
      </div>
    </div>
  );
}

function MiniTable({ rows, loading }) {
  if (loading) return <div style={{ padding:'1rem', color:'rgba(245,235,221,0.3)', fontSize:12 }}>Loading...</div>;
  if (!rows.length) return <div style={{ padding:'1rem', color:'rgba(245,235,221,0.3)', fontSize:12 }}>No influencers found.</div>;
  const th = { padding:'7px 12px', fontSize:10, color:'rgba(245,235,221,0.35)', textTransform:'uppercase', letterSpacing:0.8, borderBottom:'0.5px solid rgba(245,235,221,0.08)', background:'rgba(0,0,0,0.2)', textAlign:'left', whiteSpace:'nowrap' };
  const td = { padding:'7px 12px', fontSize:12, color:'#F5EBDD', borderBottom:'0.5px solid rgba(245,235,221,0.04)', whiteSpace:'nowrap' };
  return (
    <div style={{ overflowX:'auto', maxHeight:260, overflowY:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead style={{ position:'sticky', top:0 }}>
          <tr><th style={th}>Name</th><th style={th}>Handle</th><th style={th}>Tier</th><th style={th}>Followers</th><th style={th}>City</th><th style={th}>State</th></tr>
        </thead>
        <tbody>
          {rows.slice(0,25).map(r => (
            <tr key={r.id} onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.025)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <td style={{...td,fontWeight:500}}>{r.influencer_name}</td>
              <td style={{...td,fontSize:11,color:'rgba(255,255,255,0.5)',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis'}}>{r.handle||'—'}</td>
              <td style={td}><span style={{ background:`${TIER_COLORS[r.tier]}22`, color:TIER_COLORS[r.tier], borderRadius:20, padding:'1px 8px', fontSize:10 }}>{r.tier}</span></td>
              <td style={td}>{fmtK(r.followers)}</td>
              <td style={{...td,color:'rgba(255,255,255,0.45)'}}>{r.city||'—'}</td>
              <td style={{...td,color:'rgba(255,255,255,0.45)'}}>{r.state||'—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function StatsPanel({ platform = null }) {
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [activeFilter, setActiveFilter] = useState(null); // {type, value, filterKey}
  const [drillRows, setDrillRows]   = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillLabel, setDrillLabel] = useState('');
  const [nicheFilter, setNicheFilter] = useState('');
  const [geoTab, setGeoTab]         = useState('country'); // country | state | city

  useEffect(() => {
    setLoading(true);
    fetchStats(platform)
      .then(setStats)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [platform]);

  const drill = useCallback(async (filters, label) => {
    setDrillLoading(true); setDrillLabel(label);
    try {
      const targets = platform ? [platform] : ['ig','moj','snapchat','reddit','yt','linkedin','twitter'];
      const all = await Promise.all(targets.map(p => fetchInfluencers(p, filters).then(r => r.data)));
      setDrillRows(all.flat());
    } catch(e) { setDrillRows([]); }
    finally { setDrillLoading(false); }
  }, [platform]);

  function handleClick(type, item, filterKey, filterValue) {
    const key = `${type}:${filterValue}`;
    if (activeFilter?.key === key) {
      setActiveFilter(null); setDrillRows([]); setDrillLabel('');
    } else {
      setActiveFilter({ key, type });
      drill({ [filterKey]: filterValue }, `${type}: ${filterValue}`);
    }
  }

  function clearDrill() { setActiveFilter(null); setDrillRows([]); setDrillLabel(''); }

  if (loading) return <div style={{ padding:'3rem', color:'rgba(245,235,221,0.3)', textAlign:'center' }}>Loading dashboard...</div>;
  if (error)   return <div style={{ padding:'2rem', color:'#E24B4A' }}>{error}</div>;
  if (!stats)  return null;

  const filteredNiches = nicheFilter ? stats.by_niche.filter(n=>n.niche.toLowerCase().includes(nicheFilter.toLowerCase())) : stats.by_niche;
  const geoData = geoTab === 'country' ? stats.by_country : geoTab === 'state' ? stats.by_state : stats.by_city;
  const geoKey  = geoTab === 'country' ? 'country' : geoTab === 'state' ? 'state' : 'city';

  return (
    <div style={{ padding:'1.5rem 2rem' }}>

      {/* Stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:10, marginBottom:'1.5rem' }}>
        <StatCard label="Total Influencers" value={stats.total} sub="click to view all"
          onClick={() => { setActiveFilter({key:'all'}); drill({}, 'All Influencers'); }} active={activeFilter?.key==='all'} />
        <StatCard label="Total Reach"    value={fmtK(stats.total_reach)}       sub="all platforms" />
        <StatCard label="Avg Followers"  value={fmtK(stats.avg_followers)}      sub="per influencer" />
        <StatCard label="Avg Engagement" value={`${stats.avg_engagement || 0}%`} sub="rate" />
        {stats.by_tier?.map(t => (
          <StatCard key={t.tier} label={t.tier} value={t.count} sub={fmtK(t.reach)+' reach'}
            active={activeFilter?.key===`tier:${t.tier}`}
            onClick={() => handleClick('Tier', t, 'tier', t.tier)}
          />
        ))}
      </div>

      {/* Per-platform summary on dashboard */}
      {stats.per_platform && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px,1fr))', gap:10, marginBottom:'1.5rem' }}>
          {Object.entries(stats.per_platform).map(([p, s]) => (
            <div key={p} style={{ background:'#242420', border:'0.5px solid rgba(245,235,221,0.08)', borderRadius:10, padding:'0.75rem 1rem', cursor:'pointer' }}
              onClick={() => { setActiveFilter({key:`platform:${p}`}); drill({}, `Platform: ${p.toUpperCase()}`); }}
            >
              <div style={{ fontSize:11, color:'rgba(245,235,221,0.4)', textTransform:'uppercase', letterSpacing:0.8 }}>{p}</div>
              <div style={{ fontSize:20, fontWeight:600, color:'#F5EBDD', marginTop:4 }}>{s.total}</div>
              <div style={{ fontSize:10, color:'rgba(245,235,221,0.3)' }}>{fmtK(s.total_reach)} reach</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:'1.5rem' }}>

        {/* By Niche */}
        <div style={{ background:'#242420', border:'0.5px solid rgba(245,235,221,0.08)', borderRadius:14, padding:'1.25rem 1.5rem' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem' }}>
            <span style={{ fontSize:13, fontWeight:600 }}>By Niche</span>
            <input style={{ background:'rgba(245,235,221,0.05)', border:'0.5px solid rgba(255,255,255,0.1)', borderRadius:6, padding:'4px 8px', fontSize:11, color:'#F5EBDD', outline:'none', width:110 }}
              placeholder="Filter..." value={nicheFilter} onChange={e=>setNicheFilter(e.target.value)} />
          </div>
          {filteredNiches.map((n,i) => (
            <BarRow key={n.niche} labelKey="niche" item={n} total={stats.total}
              color={COLORS[i%COLORS.length]}
              active={activeFilter?.key===`niche:${n.niche}`}
              onClick={()=>handleClick('Niche',n,'niche',n.niche)}
            />
          ))}
        </div>

        {/* Geographic breakdown */}
        <div style={{ background:'#242420', border:'0.5px solid rgba(245,235,221,0.08)', borderRadius:14, padding:'1.25rem 1.5rem' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem' }}>
            <span style={{ fontSize:13, fontWeight:600 }}>Geographic</span>
            <div style={{ display:'flex', gap:4 }}>
              {['country','state','city'].map(t => (
                <button key={t} onClick={()=>setGeoTab(t)} style={{ background:geoTab===t?'rgba(229,93,74,0.2)':'none', border:`0.5px solid ${geoTab===t?'#E55D4A':'rgba(255,255,255,0.1)'}`, borderRadius:5, padding:'3px 8px', fontSize:11, color:geoTab===t?'#F5EBDD':'rgba(245,235,221,0.4)', cursor:'pointer', fontFamily:'inherit', textTransform:'capitalize' }}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          {geoData?.map((item,i) => (
            <BarRow key={item[geoKey]} labelKey={geoKey} item={item} total={stats.total}
              color={COLORS[i%COLORS.length]}
              active={activeFilter?.key===`${geoKey}:${item[geoKey]}`}
              onClick={()=>handleClick(geoKey.charAt(0).toUpperCase()+geoKey.slice(1), item, geoKey, item[geoKey])}
            />
          ))}
        </div>
      </div>

      {/* Language + Relationship */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:'1.5rem' }}>
        <div style={{ background:'#242420', border:'0.5px solid rgba(245,235,221,0.08)', borderRadius:14, padding:'1.25rem 1.5rem' }}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:'0.75rem' }}>By Language</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px,1fr))', gap:8 }}>
            {stats.by_language?.map((l,i) => (
              <div key={l.language} onClick={()=>handleClick('Language',l,'language',l.language)}
                style={{ background: activeFilter?.key===`language:${l.language}` ? 'rgba(245,235,221,0.07)':'rgba(245,235,221,0.04)', borderRadius:7, padding:'8px 12px', display:'flex', justifyContent:'space-between', cursor:'pointer', transition:'background 0.15s' }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(245,235,221,0.07)'}
                onMouseLeave={e=>e.currentTarget.style.background=activeFilter?.key===`language:${l.language}`?'rgba(245,235,221,0.07)':'rgba(245,235,221,0.04)'}
              >
                <span style={{ fontSize:12 }}>{l.language}</span>
                <span style={{ fontSize:12, fontWeight:600, color:COLORS[i%COLORS.length] }}>{l.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background:'#242420', border:'0.5px solid rgba(245,235,221,0.08)', borderRadius:14, padding:'1.25rem 1.5rem' }}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:'0.75rem' }}>By Relationship Status</div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {stats.by_relationship?.map((r,i) => (
              <div key={r.status} onClick={()=>handleClick('Relationship',r,'relationship',r.status)}
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:activeFilter?.key===`relationship:${r.status}`?'rgba(245,235,221,0.06)':'rgba(255,255,255,0.03)', borderRadius:7, padding:'8px 12px', cursor:'pointer' }}
              >
                <span style={{ fontSize:12 }}>{r.status}</span>
                <span style={{ fontSize:12, fontWeight:600, color:COLORS[i%COLORS.length] }}>{r.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Drill-down panel */}
      {(drillLabel || drillLoading) && (
        <div style={{ background:'#242420', border:'0.5px solid rgba(229,93,74,0.3)', borderRadius:14, padding:'1.25rem 1.5rem' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem' }}>
            <div style={{ fontSize:13, fontWeight:600 }}>
              {drillLabel}
              {!drillLoading && <span style={{ marginLeft:8, fontSize:11, color:'rgba(245,235,221,0.35)', fontWeight:400 }}>{drillRows.length} influencer{drillRows.length!==1?'s':''}</span>}
            </div>
            <button onClick={clearDrill} style={{ background:'none', border:'0.5px solid rgba(255,255,255,0.15)', borderRadius:6, color:'rgba(245,235,221,0.4)', fontSize:11, padding:'3px 10px', cursor:'pointer', fontFamily:'inherit' }}>Clear</button>
          </div>
          <MiniTable rows={drillRows} loading={drillLoading} />
        </div>
      )}
    </div>
  );
}
